import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ResizeObservable } from '@shared/util/dom/resize.observable';
import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, OnDestroy } from '@angular/core';
import {
  Chart,
  ChartAxisXOptions,
  ChartAxisYOptions,
  ChartDataPoint,
  ChartDataSeries,
  ChartDataSeriesOptions,
  ChartEvent,
  ChartLegendOptions,
  ChartOptions,
  ChartToolTipOptions
} from 'canvasjs';
import { ResizableDirective, ResizeEvent } from 'angular-resizable-element';
import { AceLoggerService } from '@logging/ace-logger.service';
import { AceLogger } from '@logging/ace-logger';
import { SiteInfo } from '@shared/services/api/access-sites.interface';
import { ColourGenerationService } from '@shared/services/util/colours/colour-generation.service';
import { MathUtils } from '@shared/util/general/math-utils';
import { BootstrapService, BootstrapBreakpoint, MessageTranslateService } from '@shared/services';
import { AppConstants } from "@shared/util/app-constants";
import { ChartsUtil } from '@app/layout/charts/charts-util';
import { I18nMessage } from '@shared/api/model/i18n';
import { ChartService } from '@app/layout/charts/chart.service';
import {ChartComponent} from "@shared/model/chart-component.interface";

export enum CHART_CSS_CLASS {
  DYNAMIC = 'dynamicChartContainer',
  SPLIT = 'splitChartContainer',
  BINARY_ONLY = 'binaryOnlyChartContainer',
  BINARY_AND_ALERT = 'binaryAndAlertsChartContainer',
  FULL_BROWSER_HEIGHT = 'fullBrowserHeightChartContainer'
}

export enum CHART_HEIGHT {
  DYNAMIC = '81vh',
  SPLIT = '175px',
  BINARY_ONLY = '75px',
  BINARY_AND_ALERT = '100px',
  FULL_BROWSER_HEIGHT = '100vh'
}


export enum RESIZE_EVENT_STATUS {
  STARTED = 'started',
  STOPPED = 'stopped'
}

export interface ChartResizeEvent {
  status: RESIZE_EVENT_STATUS;
  height: number;
}


export enum ChartSeriesType {
  FAVOURITE = 'Favourite',
  ALERT = 'Alert',
  METER_STREAM = 'MeterStream',
  THERMAL_COMFORT_SCORES = 'Thermal Comfort Scores',
}


export interface ChartSeriesData {
  type: ChartSeriesType;
  id: number | string;
  name: string;
}


// Chart will ignore label if x exist so add a new property for x called xValue
// Then use getX() to get the x value which will return xValue if label otherwise x
// So we can still access xValue and chart will use label
export interface LabelChartDataPoint extends ChartDataPoint {
  xValue?: number | Date;
  /** Part of datapoint value that is estimated. Absolute value to know the amount of data that is estimated. */
  absoluteEstimate?: number;
  /** Total datapoint absolute value to be able to tell how much percent the estimate is of actual resulting data */
  absoluteBreakdownTotal?: number;
}

export interface ChartDataPointSelectEvent extends ChartEvent {
  selected: boolean;
}


export interface DataPointCoordinates {
  dataSeriesIndex: number;
  dataPointIndex: number;
}

export interface TooltipFormatterEntry {
  chart: Chart,
  toolTip: ChartToolTipOptions;
  entries: Array<{ dataPoint: ChartDataPoint; dataSeries: ChartDataSeriesOptions }>;
}

export interface ChartResponsivity {
  breakpoint: BootstrapBreakpoint;
  chartType?: string;
}

export interface ChartMenuItem {
  label: I18nMessage;
  position: InsertPosition;
  callback: () => void;
  customDataAttribute?: string;
}

interface ResponsivityData {
  responsiveSize: boolean;
  responsivityWidth: number;
  originalChartSeries: ChartDataSeriesOptions[]
}

@Component({
  selector: 'app-canvas-chart',
  templateUrl: './canvas-chart.component.html',
  styleUrls: ['./canvas-chart.component.scss'],
  standalone: false
})
export class CanvasChartComponent implements OnChanges, AfterViewInit, AfterViewChecked, OnDestroy, ChartComponent {

  private readonly logger: AceLogger;
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly CHART_MIN_HEIGHT_PX = 100;
  private readonly RESIZE_SCROLL_MARGIN = 20;
  private readonly RESIZE_DEFAULT_SCROLL_OFFSET = 56; // The height of the 'fixed' bar at the top of the page
  private readonly DATA_SERIES_OPACITY_WHEN_DATA_POINT_SELECTED = 20;
  private readonly DEFAULT_RESPONSIVE_CHART_TYPE = 'bar';
  private readonly CHART_TOOLBAR_SELECTOR = '.canvasjs-chart-toolbar';

  private readonly getColour: (index: number) => string;
  private delayedRenderTimeout: ReturnType<typeof setTimeout>;

  @Input() allowSelectDataPoint = false;
  @Input() chartOptions: ChartOptions = null;
  @Input() showMenu = false;
  @Input() menuItems: ChartMenuItem[];
  @Input() chartHeight: string;
  @Input() allowResize = true;
  @Input() splitMode = false;
  @Input() fullBrowserHeight: boolean;
  @Input() site: SiteInfo;
  @Input() selectedDataCoordinates: DataPointCoordinates = null;
  @Input() responsivity: ChartResponsivity = null;

  @Output() toggleSeriesEvent = new EventEmitter<ChartEvent>();
  @Output() resizeEvent = new EventEmitter<ChartResizeEvent>();
  @Output() selectDataPointEvent = new EventEmitter<ChartDataPointSelectEvent>();
  @Output() chartRendered = new EventEmitter<void>();

  @ViewChild('chartContainer')
  chartContainer: ElementRef;

  @ViewChild(ResizableDirective)
  resizableContainer: ResizableDirective;

  chart: Chart;
  chartClass: CHART_CSS_CLASS = CHART_CSS_CLASS.DYNAMIC;
  // Used to record the configured value of chart height that was overridden by manually resizing the chart
  overriddenChartHeight: string = null;

  private updateMenuItems: boolean;
  private menuItemDataAttributes = new Set<string>();
  private chartOptionsUpdated = false;
  private sizeChanged = false;
  private resizeInProgress = false;
  private resizeStartCoordsTopOffset: number = null;

  private responsivityData: ResponsivityData = {responsiveSize: false, responsivityWidth: null, originalChartSeries: null};

  private readonly defaultLegendOption: ChartLegendOptions = {
    fontSize: this.splitMode ? 10 : 12,
    cursor: 'pointer',
    verticalAlign: 'bottom',
    horizontalAlign: 'center'
  };

  private readonly defaultAxisXOption: ChartAxisXOptions = <ChartAxisXOptions>{
    crosshair: {
      enabled: true,
      snapToDataPoint: true,
      labelFontSize: 9,
    },
    labelAngle: -20,
    fontWeight: 'normal',
    labelFontSize: 11,
    labelMaxWidth: 170,
    labelWrap: true,
  };

  private readonly defaultAxisYOption: ChartAxisYOptions = <ChartAxisYOptions>{
    crosshair: {
      enabled: false
    },
    fontWeight: 'normal',
    labelFontSize: 11,
  };

  private readonly defaultAxisY2Option: ChartAxisYOptions = {
    lineThickness: 0,
    tickThickness: 0,
  };

  constructor(private translateService: MessageTranslateService, private bootstrapService: BootstrapService,
              private chartService: ChartService, colourGenerationService: ColourGenerationService,
              loggerService: AceLoggerService) {
    const { getColour } = colourGenerationService.getDefaultProfileColourAccessors();
    this.getColour = getColour;
    this.logger = loggerService.create(CanvasChartComponent.name);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes.chartHeight) {
        this.resetOverriddenChartHeight();
        if (changes.chartHeight.currentValue == null) {
          if (!this.splitMode && !this.fullBrowserHeight) {
            // This should only happen when using a single chart which is not in Full Browser Height mode
            this.chartHeight = CHART_HEIGHT.DYNAMIC;
          } else {
            // This only happens while a change is in flight, i.e. a user has chosen to split the charts.
            // The charts will be recreated anyway, so ignoring the change.
          }
        }
      }

      if (changes.chartOptions) {
        this.addDefaultChartOptions(this.chartOptions);

        if (this.responsivityData.responsiveSize) {
          const changed = this.changeToResponsiveChartType(this.chartOptions);
          if (!changed) {
            this.responsivityData.originalChartSeries = null;
          }
        }

        if (this.selectedDataCoordinates) {
          if (this.chartOptions == null || !this.dataPointCoordinatesValid(this.selectedDataCoordinates, this.chartOptions)) {
            if (changes.selectedDataCoordinates) {
              // If the selected data coordinates have been explicitly set and are invalid, throw an error
              throw new Error('Invalid data option ' + JSON.stringify(this.selectedDataCoordinates));
            } else {
              // Otherwise, clear them
              this.selectedDataCoordinates = null;
              this.selectDataPointEvent.emit(null);
            }
          }
        }
      } else if (changes.selectedDataCoordinates) {
        // Chart options are not changing, just a different point selection is being made (or being cleared)
        if (this.chart) {
          this.processDataPointSelection(changes.selectedDataCoordinates.previousValue, this.selectedDataCoordinates, this.chart, true);
        } else if (this.selectedDataCoordinates) {
          throw new Error('Cannot select ' + JSON.stringify(this.selectedDataCoordinates) + ' as chart does not exist');
        }
      }

      if (changes.responsivity) {
        if (this.responsivity) {
          const width = this.bootstrapService.getBreakpointBoundary(this.responsivity.breakpoint);
          if (width != null) {
            this.responsivityData.responsivityWidth = width;
            this.handleResponsivityChangeIfNeeded(this.chartContainer?.nativeElement?.getBoundingClientRect()?.width);
          } else {
            throw new Error('Invalid breakpoint name "' + this.responsivity.breakpoint + '"');
          }
        } else {
          this.responsivityData.responsivityWidth = null;
          this.responsivityData.responsiveSize = false;
          this.handleLargerMedia();
        }
      }

      if (changes.menuItems) {
        this.updateMenuItems = true;
      }
    }
  }

  ngAfterViewInit(): void {
    this.chart = this.initChart(this.chartOptions);
    this.listenForResizeEvents();
  }

  ngAfterViewChecked() {
    if (this.sizeChanged || this.chartOptionsUpdated) {
      if (this.chartOptionsUpdated) {
        // if chart options are different, rebuild the chart as canvasJS does not have a convenient method to update the whole options.
        this.chart = this.initChart(this.chartOptions, true);
        // canvasJS draws the toolbar as part of the chart, so the rebuild above replaced it with a
        // fresh one that has none of the custom menu items on it
        this.updateMenuItems = true;
      } else {
        this.chart.render();
      }
      this.sizeChanged = false;
      this.chartOptionsUpdated = false;
    }

    if (this.updateMenuItems) {
      this.addMenuItems();
      this.updateMenuItems = false;
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.destroyChart();
  }

  validateResize = (event: ResizeEvent): boolean => {
    return event.rectangle.height >= this.CHART_MIN_HEIGHT_PX;
  };

  onResizeStart(event: ResizeEvent) {
    this.resizeInProgress = true;
    this.resizeEvent.emit({ status: RESIZE_EVENT_STATUS.STARTED, height: event.rectangle.height });
  }

  onResize(event: ResizeEvent) {
    const top = this.resizableContainer?.elm?.nativeElement?.getBoundingClientRect()?.top;
    if (top != null) {
      const bottom = top + (event.rectangle.bottom - event.rectangle.top);
      const bottomBoundary = window.innerHeight - this.RESIZE_SCROLL_MARGIN;
      if (bottom > bottomBoundary) {
        const scrollDistance = bottom - bottomBoundary;
        window.scrollTo(window.scrollX, window.scrollY + scrollDistance);
      } else {
        const topBoundary = this.getResizeCoordsTopOffset() + this.RESIZE_SCROLL_MARGIN;
        if (bottom < topBoundary) {
          const scrollDistance = bottom - topBoundary;
          window.scrollTo(window.scrollX, window.scrollY + scrollDistance);
        }
      }
    } else {
      this.logger.warn('Cannot determine if page needs to scroll as cannot retrieve the bounding rect of resize container');
    }
  }

  onResizeEnd(event: ResizeEvent) {
    if (this.overriddenChartHeight == null) {
      this.overriddenChartHeight = this.chartHeight;
    }
    this.chartHeight = event.rectangle.height + 'px';
    this.resizeInProgress = false;
    this.sizeChanged = true;

    this.resizeEvent.emit({ status: RESIZE_EVENT_STATUS.STOPPED, height: event.rectangle.height });
  }

  resetHeight(): void {
    this.chartHeight = this.overriddenChartHeight;
    this.sizeChanged = true;
    this.resetOverriddenChartHeight();
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private isSameDataPoint(dataPoint1: DataPointCoordinates, dataPoint2: DataPointCoordinates) {
    return (dataPoint1?.dataSeriesIndex ?? null) === (dataPoint2?.dataSeriesIndex ?? null) &&
      (dataPoint1?.dataPointIndex ?? null) === (dataPoint2?.dataPointIndex ?? null);
  }

  private buildChart(chartOptions: ChartOptions): Chart {
    return chartOptions ? this.chartService.buildChart(this.chartContainer.nativeElement, chartOptions) : null;
  }

  private initChart(chartOptions: ChartOptions, forceRebuildChart = false): Chart {
    const chart = (this.chart == null || forceRebuildChart) ? this.buildChart(chartOptions) : this.chart;
    if (chart) {
      // Need to call this before any point selection as otherwise chart does not have any data in it
      chart.render();
      if (this.selectedDataCoordinates) {
        // Ensure the selected data point is highlighted
        this.selectDataPoint(chart, null, this.selectedDataCoordinates);
        chart.render();
      }
    }
    return chart;
  }

  private addDefaultChartOptions(chartOptions: ChartOptions) {
    chartOptions.legend = {
      ...this.defaultLegendOption,
      ...chartOptions.legend
    };
    chartOptions.title = {
      fontSize: this.splitMode ? 13 : 15,
      ...chartOptions.title
    };

    if (chartOptions.axisX) {
      if (Array.isArray(chartOptions.axisX)) {
        chartOptions.axisX = chartOptions.axisX.map(axis => ({
          ...this.defaultAxisXOption,
          ...axis
        }));
      } else {
        chartOptions.axisX = {
          ...this.defaultAxisXOption,
          ...chartOptions.axisX
        };
      }
    }

    chartOptions.axisY = {
      ...this.defaultAxisYOption,
      ...chartOptions.axisY
    };

    chartOptions.axisY2 = {
      ...this.defaultAxisYOption,
      ...this.defaultAxisY2Option,
      ...chartOptions.axisY2,
    };

    this.chartOptions.exportEnabled = this.menuItems?.length > 0 || this.showMenu;

    if (chartOptions.data?.length) {
      chartOptions.legend.itemclick = this.onToggleChartLegend.bind(this);
      this.addDefaultDataSeriesOptions(chartOptions.data);
    }

    this.chartOptionsUpdated = true;
  }

  private addDefaultDataSeriesOptions(dataSeries: ChartDataSeriesOptions[]) {
    dataSeries.forEach((dataSeries, dataSeriesIndex) => {
      if (dataSeries.color == null) {
        const seriesColour = this.getColour(dataSeriesIndex);
        dataSeries.color = seriesColour;
        // set legend marker colour separately
        // this is to make sure when data series colour changes due to data point selection (adding opacity)
        // legend marker color is not affected
        dataSeries.legendMarkerColor = seriesColour;
      }
      if (this.allowSelectDataPoint) {
        dataSeries.dataPoints.forEach((dataPoint: ChartDataPoint) => Object.assign(dataPoint, {
          click: this.onSelectDataPointChartEvent.bind(this),
          cursor: 'pointer'
        }));
      }
    });
  }

  private onToggleChartLegend(event: ChartEvent) {
    event.dataSeries.visible = !this.isSeriesVisible(event.dataSeries);

    if (!event.dataSeries.visible && this.selectedDataCoordinates && event.dataSeriesIndex === this.selectedDataCoordinates.dataSeriesIndex) {
      // If the series that has a selected data point is being hidden, unselect the selected data point
      this.processDataPointSelection(this.selectedDataCoordinates, null, event.chart);
    }

    event.chart.render();
  }

  private getChartDataPoint(chart: Chart, dataSeriesIndex: number, dataPointIndex: number): ChartDataPoint | null {
    const dataSeries = this.getChartDataSeries(chart, dataSeriesIndex);
    return this.getChartDataPointFromDataSeries(dataSeries, dataPointIndex);
  }

  private getChartDataSeries(chart: Chart, dataSeriesIndex: number): ChartDataSeries | null {
    return dataSeriesIndex < (chart.data?.length || 0) ? chart.data[dataSeriesIndex] : null;
  }

  private getChartDataPointFromDataSeries(dataSeries: ChartDataSeries, dataPointIndex: number): ChartDataPoint | null {
    return dataSeries != null && dataPointIndex < (dataSeries.dataPoints?.length || 0) ? dataSeries.dataPoints[dataPointIndex] : null;
  }

  private deselectDataPoint(chart: Chart, dataPointCoordinates: DataPointCoordinates): void {
    // has previous data point selected, should reset previous data point's color
    const previousDataPoint = this.getChartDataPoint(chart, dataPointCoordinates.dataSeriesIndex, dataPointCoordinates.dataPointIndex);
    if (previousDataPoint) {
      delete previousDataPoint.color;
    }
  }

  private getColourHexCodeWithOpacity(colourHexCode: string, opacity: number | null): string {
    const colourCodeWithHashPrefix = colourHexCode.startsWith('#');
    const colourCodeWithoutOpacityLength = colourCodeWithHashPrefix ? 7 : 6;
    const isHexCodeWithoutOpacity = colourHexCode.length === colourCodeWithoutOpacityLength;
    if (opacity == null || opacity === 100) {
      // remove opacity
      return isHexCodeWithoutOpacity ? colourHexCode : colourHexCode.substring(0, colourCodeWithoutOpacityLength);
    }
    const targetOpacityHex = MathUtils.percentageToHex(opacity);
    if (isHexCodeWithoutOpacity) {
      return colourHexCode + targetOpacityHex;
    }
    const currentOpacityHex = colourHexCode.substring(colourCodeWithoutOpacityLength - 1);
    return currentOpacityHex === targetOpacityHex
        ? colourHexCode
        : (colourHexCode.substring(0, colourCodeWithoutOpacityLength) + targetOpacityHex);
  }

  private onSelectDataPointChartEvent(event: ChartEvent) {
    if (this.allowSelectDataPoint) {
      const targetDataPointCoordinate = {dataSeriesIndex: event.dataSeriesIndex, dataPointIndex: event.dataPointIndex};
      const unselectDataPoint = this.isSameDataPoint(this.selectedDataCoordinates, targetDataPointCoordinate);
      this.processDataPointSelection(this.selectedDataCoordinates, unselectDataPoint ? null : targetDataPointCoordinate, event.chart);
    }
  }

  private processDataPointSelection(selectedCoordinates: DataPointCoordinates, coordinatesToSelect: DataPointCoordinates, chart: Chart, suppressEvent = false): void {
    if (!this.isSameDataPoint(selectedCoordinates, coordinatesToSelect)) {
      let event: ChartDataPointSelectEvent = null;
      if (coordinatesToSelect == null) {
        if (selectedCoordinates) {
          this.deselectDataPoint(chart, selectedCoordinates);
          // deselect previously selected data point, reset all data series colours back to no opacity.
          chart.data?.forEach(dataSeries => dataSeries.set('color', this.getColourHexCodeWithOpacity(dataSeries.color, null), false));
          event = this.buildChartSelectedEventFromDataPointCoordinates(false, selectedCoordinates, chart);
        }
      } else {
        const {dataSeries, dataPoint} = this.selectDataPoint(chart, selectedCoordinates, coordinatesToSelect);
        event = this.buildChartSelectedEvent(true, coordinatesToSelect, dataSeries, dataPoint, chart);
      }

      if (event != null) {
        chart.render();
        this.selectedDataCoordinates = event.selected ? {dataSeriesIndex: event.dataSeriesIndex, dataPointIndex: event.dataPointIndex}: null;
        if (!suppressEvent) {
          this.selectDataPointEvent.emit(event);
        }
      }
    }
  }

  // eslint-disable-next-line max-len
  private selectDataPoint(chart: Chart, selectedCoordinates: DataPointCoordinates, coordinatesToSelect: DataPointCoordinates):{dataSeries: ChartDataSeries, dataPoint: ChartDataPoint} {
    if (selectedCoordinates) {
      this.deselectDataPoint(chart, selectedCoordinates);
    } else {
      // if there's no previously selected data point, should set all data series to specified opacity
      chart.data?.forEach(dataSeries =>
        dataSeries.set('color', this.getColourHexCodeWithOpacity(dataSeries.color, this.DATA_SERIES_OPACITY_WHEN_DATA_POINT_SELECTED), false));
    }
    const dataSeries = this.getChartDataSeries(chart, coordinatesToSelect.dataSeriesIndex);
    const dataPoint = this.getChartDataPointFromDataSeries(dataSeries, coordinatesToSelect.dataPointIndex);
    dataPoint.color = this.getColourHexCodeWithOpacity(dataSeries.color, null);
    return {dataSeries, dataPoint};
  }

  private buildChartSelectedEventFromDataPointCoordinates(selected: boolean, dataPointCoordinates: DataPointCoordinates, chart: Chart): ChartDataPointSelectEvent {
    const dataSeries = this.getChartDataSeries(chart, dataPointCoordinates.dataSeriesIndex);
    const dataPoint = this.getChartDataPointFromDataSeries(dataSeries, dataPointCoordinates.dataPointIndex);
    return this.buildChartSelectedEvent(selected, dataPointCoordinates, dataSeries, dataPoint, chart);
  }

  private buildChartSelectedEvent(selected: boolean, dataCoordinates: DataPointCoordinates, dataSeries: ChartDataSeries,
                                  dataPoint: ChartDataPoint, chart: Chart): ChartDataPointSelectEvent {
    return {selected: selected, dataPoint: dataPoint, x: dataPoint.x, y: dataPoint.y, dataPointIndex: dataCoordinates.dataPointIndex,
            dataSeries: dataSeries, dataSeriesIndex: dataCoordinates.dataSeriesIndex, chart: chart};
  }


  private isSeriesVisible: (series: ChartDataSeriesOptions) => boolean = (series: ChartDataSeriesOptions) => {
    // The 'visible' property defaults to true, so treat undefined as a true value. Therefore, if a series with undefined visible
    // property has been clicked, it should be made invisible.
    return typeof series.visible === 'undefined' || series.visible;
  };

  private getResizeCoordsTopOffset(): number {
    if (this.resizeStartCoordsTopOffset == null) {
      const mainContainerElements = document.getElementsByClassName(AppConstants.MAIN_CONTAINER_CLASS_NAME);
      if (mainContainerElements && mainContainerElements.length === 1) {
        this.resizeStartCoordsTopOffset = window.scrollY + mainContainerElements.item(0).getBoundingClientRect().top;
      } else {
        this.resizeStartCoordsTopOffset = this.RESIZE_DEFAULT_SCROLL_OFFSET;
        this.logger.error('Cannot find a single main-container element. Using '
            + this.resizeStartCoordsTopOffset + ' as top offset. The found elements are', mainContainerElements);
      }
    }
    return this.resizeStartCoordsTopOffset;
  }

  private dataPointCoordinatesValid(dataCoordinates: DataPointCoordinates, chartOptions: ChartOptions): boolean {
    return dataCoordinates.dataSeriesIndex >=0 && dataCoordinates.dataSeriesIndex < chartOptions.data?.length &&
           dataCoordinates.dataPointIndex >= 0 && dataCoordinates.dataPointIndex < chartOptions.data[dataCoordinates.dataSeriesIndex].dataPoints?.length;
  }

  private handleSmallerMedia(): void {
    if (this.responsivityData.originalChartSeries == null) {
      this.changeToResponsiveChartType(this.chart.options);
    }
  }

  private changeToResponsiveChartType(options: ChartOptions): boolean {
    const chartType = options?.data?.length && options.data[0].type;
    if (chartType != null && this.chart.options.data.every(series => series.type === chartType)) {
      const newChartSeries: ChartDataSeriesOptions[] = options.data.map(series => ({...series, type: this.responsivity?.chartType || this.DEFAULT_RESPONSIVE_CHART_TYPE}));
      this.responsivityData.originalChartSeries = options?.data ?? null;
      options.data = newChartSeries;
      this.delayedRender();
      return true;
    } else {
      return false;
    }
  }

  private handleLargerMedia(): void {
    if (this.responsivityData.originalChartSeries != null) {
      if (this.chart?.options) {
        this.chart.options.data = this.responsivityData.originalChartSeries;
      }
      this.responsivityData.originalChartSeries = null;
      this.delayedRender();
    }
  }

  private listenForResizeEvents(): void {
    new ResizeObservable(this.chartContainer.nativeElement)
      .pipe(debounceTime(AppConstants.RESIZE_EVENT_DEBOUNCE_MS), takeUntil(this.ngUnsubscribe))
      .subscribe(entries => {
        if (entries?.length) {
          this.logger.debug('Chart resized');
          this.handleResponsivityChangeIfNeeded(entries[entries.length - 1].contentRect.width);
        }
      });
  }

  private handleResponsivityChangeIfNeeded(width: number): void {
    if (this.responsivityData.responsivityWidth != null && width != null) {
      this.responsivityData.responsiveSize = width <= this.responsivityData.responsivityWidth;
      if (this.responsivityData.responsiveSize) {
        this.handleSmallerMedia();
      } else {
        this.handleLargerMedia();
      }
    }
    this.delayedRender();
  }

  private addMenuItems(): void {
    if (this.menuItems?.length) {
      const childNodes = this.chartContainer.nativeElement.childNodes[0].querySelector(this.CHART_TOOLBAR_SELECTOR).childNodes;
      const extendedToolbar: HTMLElement = childNodes[childNodes.length - 1];
      extendedToolbar.style.fontFamily = 'inherit';
      this.removeMenuItems(extendedToolbar);
      this.menuItemDataAttributes.clear();
      const makeCallback = (item: ChartMenuItem) => {
        return () => {
          this.closeChartMenu();
          item.callback();
        };
      };
      this.menuItems.forEach(item => {
        ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant(item.label), '', item.position, makeCallback(item), item.customDataAttribute);
        this.menuItemDataAttributes.add(item.customDataAttribute || ChartsUtil.CHART_MENU_DEFAULT_DATA_ATTRIBUTE);
      });
    }
  }

  private closeChartMenu(): void {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      // Close the chart menu
      activeElement.blur();
    } else {
      this.logger.error('Cannot close the Chart Menu as active element is not a HTMLElement', activeElement);
    }
  }

  private removeMenuItems(menu: HTMLElement): void {
    menu?.childNodes.forEach((node: HTMLElement) => {
      if (node.getAttributeNames().some(attr => this.menuItemDataAttributes.has(attr))) {
        node.remove();
      }
    });
  }

  private resetOverriddenChartHeight(): void {
    this.overriddenChartHeight = null;
  }

  /** Can be called multiple times but will only actually call chart render once after current sync code is done */
  delayedRender(): void {
    if (this.delayedRenderTimeout != null) {
      clearTimeout(this.delayedRenderTimeout);
    }
    this.delayedRenderTimeout = setTimeout(() => {
      if (this.chart) {
        this.chart.render();
        this.chartRendered.emit();
      }
    });
  }
}
